import { Fragment, type ReactNode } from 'react';
import '../styles/markdown-view.scss';

/**
 * Read-only markdown renderer for stored notes (check-in summaries, steps).
 *
 * Deliberately builds React elements instead of an HTML string: nothing here can
 * inject markup, so there is no sanitizer to keep correct. The knowledge editor
 * (tiptap) stays the tool for *writing*; mounting a ProseMirror instance per row
 * just to display a paragraph would be far heavier than this.
 *
 * Supported: headings, bullet/ordered lists, blockquotes, fenced code, rules,
 * and inline code / bold / italic / strike / links.
 */

const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

// The link target tolerates one nested pair of parentheses, so that both a real
// URL that contains them - Wikipedia's `Foo_(bar)` - and a rejected
// `javascript:alert(1)` are consumed whole instead of leaving a stray `)` behind.
const LINK_TARGET = '\\((?:[^()\\s]|\\([^()\\s]*\\))*\\)';
const INLINE = new RegExp(
  '(`[^`\\n]+`)' +
    '|(\\*\\*[^*\\n]+\\*\\*)' +
    '|(~~[^~\\n]+~~)' +
    '|(\\*[^*\\n]+\\*)' +
    `|(\\[[^\\]\\n]*\\]${LINK_TARGET})`,
  'g'
);

/** Splits one line of text into React nodes, honouring inline markdown. */
function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  INLINE.lastIndex = 0;
  let i = 0;

  const plain = (s: string) => {
    // Preserve hard line breaks inside a paragraph - a summary's own line
    // structure is part of what makes it readable.
    s.split('\n').forEach((part, idx) => {
      if (idx) out.push(<br key={`${keyBase}-br-${i++}`} />);
      if (part) out.push(part);
    });
  };

  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) plain(text.slice(last, m.index));
    const key = `${keyBase}-i-${i++}`;
    if (m[1]) out.push(<code key={key}>{m[1].slice(1, -1)}</code>);
    else if (m[2]) out.push(<strong key={key}>{m[2].slice(2, -2)}</strong>);
    else if (m[3]) out.push(<s key={key}>{m[3].slice(2, -2)}</s>);
    else if (m[4]) out.push(<em key={key}>{m[4].slice(1, -1)}</em>);
    else if (m[5]) {
      const cut = m[5].indexOf('](');
      const label = m[5].slice(1, cut);
      const href = m[5].slice(cut + 2, -1);
      out.push(
        SAFE_HREF.test(href) ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {label || href}
          </a>
        ) : (
          // Anything else (javascript:, data:, knowledge:) is shown, never linked.
          <Fragment key={key}>{label || href}</Fragment>
        )
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) plain(text.slice(last));
  return out;
}

type Block =
  | { t: 'h'; level: number; text: string }
  | { t: 'p'; text: string }
  | { t: 'ul' | 'ol'; items: string[] }
  | { t: 'quote'; text: string }
  | { t: 'code'; text: string }
  | { t: 'hr' };

function parse(md: string): Block[] {
  const lines = md.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];

  const flush = () => {
    if (para.length) blocks.push({ t: 'p', text: para.join('\n') });
    para = [];
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    if (/^\s*```/.test(line)) {
      flush();
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !/^\s*```/.test(lines[i])) {
        body.push(lines[i]);
        i += 1;
      }
      blocks.push({ t: 'code', text: body.join('\n') });
      continue;
    }

    if (!line.trim()) {
      flush();
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      flush();
      blocks.push({ t: 'h', level: heading[1].length, text: heading[2].trim() });
      continue;
    }

    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      flush();
      blocks.push({ t: 'hr' });
      continue;
    }

    const bullet = line.match(/^\s*[-*+]\s+(.*)$/);
    const ordered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (bullet || ordered) {
      flush();
      const kind: 'ul' | 'ol' = bullet ? 'ul' : 'ol';
      const items: string[] = [(bullet ?? ordered)![1]];
      while (i + 1 < lines.length) {
        const next = lines[i + 1];
        const nb = next.match(/^\s*[-*+]\s+(.*)$/);
        const no = next.match(/^\s*\d+[.)]\s+(.*)$/);
        if ((kind === 'ul' && nb) || (kind === 'ol' && no)) {
          items.push((nb ?? no)![1]);
          i += 1;
        } else if (next.trim() && /^\s{2,}\S/.test(next)) {
          // Continuation of the previous item rather than a new one.
          items[items.length - 1] += `\n${next.trim()}`;
          i += 1;
        } else {
          break;
        }
      }
      blocks.push({ t: kind, items });
      continue;
    }

    const quote = line.match(/^\s*>\s?(.*)$/);
    if (quote) {
      flush();
      const body: string[] = [quote[1]];
      while (i + 1 < lines.length) {
        const next = lines[i + 1].match(/^\s*>\s?(.*)$/);
        if (!next) break;
        body.push(next[1]);
        i += 1;
      }
      blocks.push({ t: 'quote', text: body.join('\n') });
      continue;
    }

    para.push(line);
  }
  flush();
  return blocks;
}

/** Renders stored markdown. `className` lands on the wrapper. */
export function Markdown({ text, className }: { text: string; className?: string }) {
  const blocks = parse(text ?? '');
  return (
    <div className={`mdv${className ? ` ${className}` : ''}`}>
      {blocks.map((b, idx) => {
        const key = `b${idx}`;
        switch (b.t) {
          case 'h': {
            // h1..h3 of a note sit below the page's own headings.
            const Tag = (['h4', 'h5', 'h6'] as const)[Math.min(b.level, 3) - 1];
            return (
              <Tag key={key} className={`mdv-h mdv-h${Math.min(b.level, 3)}`}>
                {inline(b.text, key)}
              </Tag>
            );
          }
          case 'ul':
            return (
              <ul key={key} className="mdv-ul">
                {b.items.map((it, j) => (
                  <li key={`${key}-${j}`}>{inline(it, `${key}-${j}`)}</li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={key} className="mdv-ol">
                {b.items.map((it, j) => (
                  <li key={`${key}-${j}`}>{inline(it, `${key}-${j}`)}</li>
                ))}
              </ol>
            );
          case 'quote':
            return (
              <blockquote key={key} className="mdv-quote">
                {inline(b.text, key)}
              </blockquote>
            );
          case 'code':
            return (
              <pre key={key} className="mdv-code">
                <code>{b.text}</code>
              </pre>
            );
          case 'hr':
            return <hr key={key} className="mdv-hr" />;
          default:
            return (
              <p key={key} className="mdv-p">
                {inline(b.text, key)}
              </p>
            );
        }
      })}
    </div>
  );
}

/**
 * Flattens markdown to one line of plain text - for collapsed rows, where the
 * markers themselves would be noise.
 */
export function stripMarkdown(md: string): string {
  return (md ?? '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/^\s*#{1,6}\s+/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/^\s*([-*+]|\d+[.)])\s+/gm, '• ')
    .replace(/^\s*([-*_])(\s*\1){2,}\s*$/gm, ' ')
    .replace(new RegExp(`\\[([^\\]\\n]*)\\]${LINK_TARGET}`, 'g'), '$1')
    .replace(/(\*\*|~~|`|\*)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
